import { isNumber } from '@ui-common/utils'
import { FormTextInput } from '@ui-components/form/FormTextInput'
import { FormTextInputProps } from '@ui-components/global/ComponentProps'
import { ChangeEvent, PropsWithChildren } from 'react'
import { FieldError, useFormContext, useFormState, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

export const MAX_BUSINESS_ID_LENGTH = 9
const CHARS_BEFORE_DASH = 7

function onInputChange(
  event: ChangeEvent<HTMLInputElement>,
  previousValue: string | undefined | null,
  wrapperSetValue: (val: string) => void,
): void {
  const newValue = event.target.value
  const trimmedValue = newValue.trim()

  const hadValue = typeof previousValue !== 'undefined' && previousValue !== null
  const isUserTypingMoreCharacters = hadValue && previousValue.length < newValue.length

  if (/^\d{8}$/.test(trimmedValue)) {
    // user pasted business id without dash 01111124
    wrapperSetValue(`${trimmedValue.substring(0, 7)}-${trimmedValue.substring(7, 8)}`)
  } else if (hadValue && previousValue.length === CHARS_BEFORE_DASH - 1 && isUserTypingMoreCharacters) {
    wrapperSetValue(`${newValue}-`)
  } else {
    wrapperSetValue(newValue.replace(/[^-\d]/, ''))
  }
}

function onKeyDown(
  e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  wrapperSetValue: (val: string) => void,
): void {
  const { value } = e.currentTarget
  if (value && value.length === MAX_BUSINESS_ID_LENGTH) {
    if (e.key === 'Backspace') {
      wrapperSetValue(value.substring(0, MAX_BUSINESS_ID_LENGTH - 1))
    }
  }
  if (value && value.length === CHARS_BEFORE_DASH) {
    if (isNumber(Number.parseInt(e.key))) {
      wrapperSetValue(`${value}-${e.key}`)
    }
  }
  if (e.key === '-' && (!value || value.length !== CHARS_BEFORE_DASH)) {
    wrapperSetValue(`${value}`)
    e.preventDefault()
  }
}

export const BusinessIdInput = (props: PropsWithChildren<FormTextInputProps>) => {
  const { name, label, error } = props

  const intl = useIntl()

  const { register, setValue, control } = useFormContext()
  const { errors } = useFormState({ control })

  const selectedValue = useWatch({ name, control })

  return (
    <FormTextInput
      {...props}
      inputMode="numeric"
      maxLength={MAX_BUSINESS_ID_LENGTH}
      label={label ?? intl.formatMessage({ id: 'common-business-id-label' })}
      error={error ?? (errors[name] as FieldError)?.message}
      onKeyDown={(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onKeyDown(event, (value) => {
          setValue(name, value)
        })
      }
      {...register(name, {
        shouldUnregister: true,
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
          onInputChange(event, selectedValue, (value) => {
            setValue(name, value)
          })
        },
      })}
    />
  )
}
